import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { ConditionalLeaderboard, ConditionalQuestionDisplay, WaitingSpinner, AnswerFeedbackCard, QuestionFeedbackSection, TimerBar, GameEndedScreen, LoadingQuestion, getPlayerHeaderLeftButton } from '../shared';
import { PairMatching } from '../shared/PairMatching/PairMatching';
import { useHostPlaying } from '../../hooks/useHostPlaying';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import { pickOptionAnimationForIndex, pickOptionDelayForIndex } from '../../constants/drunkVisionAnimations';
import '../../App.css';
import '../../styles/drunk-vision.css';
import { useTranslation } from '../../i18n';

interface Question {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  correctAnswer?: string;
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  // Picture Perfect specific fields
  isPicturePerfect?: boolean;
  imageUrl?: string;
  fallbackImageUrl?: string;
  pictureType?: string;
  displayEffect?: 'normal' | 'blur' | 'pixelate' | 'rotate-180' | 'grayscale' | 'silhouette';
  difficulty?: 'easy' | 'medium' | 'hard';
  visualContent?: string;
  visualKind?: 'emoji-grid' | 'odd-one-out' | 'symbol-sequence' | 'word-trap' | 'memory' | 'still-object';
  visualItems?: string[];
  visualBehavior?: 'memory' | 'still-object';
  stillItemIndex?: number;
  memoryHideAfterMs?: number;
  memoryRevealAtMs?: number;
  memoryRevealDurationMs?: number;
  // Pair Matching specific fields
  leftItems?: string[];
  rightItems?: string[];
  correctPairs?: Record<string, string>;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
}

interface CompetitivePlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: Question;
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const CompetitivePlayerDisplay: React.FC<CompetitivePlayerDisplayProps> = ({ roomState, socket, myPlayerId, onGoHome, onLoadingTimeout, isHost = false, onBackToGameSelection }) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<{
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
    totalQuestions: number;
    averageScore: number;
  } | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [selectedPairs, setSelectedPairs] = useState<Record<string, string>>({});
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [myScore, setMyScore] = useState(0);
  const [waitingForOthers, setWaitingForOthers] = useState(false);
  const [showInterestingFact, setShowInterestingFact] = useState(false);
  const [allPlayersAnswered, setAllPlayersAnswered] = useState(false);
  const [answeredPlayerIds, setAnsweredPlayerIds] = useState<Set<string>>(new Set());
  /** Set from answer-received when host is playing — shows "this question" points while waiting */
  const [pointsThisQuestionAck, setPointsThisQuestionAck] = useState<number | null>(null);
  const answerSubmittedTimeRef = useRef<number | null>(null);
  const previousPlayerScoresRef = useRef<Map<string, number>>(new Map());
  const isSubmittingRef = useRef<boolean>(false);
  const currentQuestionIdRef = useRef<string | undefined>(
    roomState.currentQuestion?.questionId || roomState.currentQuestion?.id || undefined
  );
  const previousQuestionIdRef = useRef<string | undefined>(
    roomState.currentQuestion?.questionId || roomState.currentQuestion?.id || undefined
  );

  const gameEndData = useGameEndScreenData(roomState);

  // Helper to reset question state
  const resetQuestion = () => {
    setSelectedAnswer(null);
    setSelectedPairs({});
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setWaitingForOthers(false);
    setShowInterestingFact(false);
    setAllPlayersAnswered(false);
    setAnsweredPlayerIds(new Set());
    setPointsThisQuestionAck(null);
    isSubmittingRef.current = false;
    answerSubmittedTimeRef.current = null;
  };

  // When server says game is finished (e.g. reconnection after missing game-ended), show game end screen
  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- gameEndData is derived from roomState
  }, [roomState.gameState, gameEndData]);

  // Sync score from roomState
  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const myPlayer = roomState.players.find(p => p.id === myPlayerId);
      if (myPlayer) {
        setMyScore(myPlayer.score);
      }
    }

    // Track which players have answered
    if (waitingForOthers && answerSubmitted && roomState.players) {
      const currentScores = new Map<string, number>();
      const newAnswered = new Set<string>();

      roomState.players.forEach(player => {
        const prevScore = previousPlayerScoresRef.current.get(player.id);
        currentScores.set(player.id, player.score);
        
        if (prevScore !== undefined && player.score > prevScore) {
          newAnswered.add(player.id);
        }
      });

      if (newAnswered.size > 0) {
        setAnsweredPlayerIds(prev => {
          const updated = new Set(prev);
          newAnswered.forEach(id => updated.add(id));
          return updated;
        });
      }

      previousPlayerScoresRef.current = currentScores;
    } else if (!waitingForOthers) {
      const currentScores = new Map<string, number>();
      if (roomState.players) {
        roomState.players.forEach(player => {
          currentScores.set(player.id, player.score);
        });
      }
      previousPlayerScoresRef.current = currentScores;
    }
  }, [roomState.players, myPlayerId, waitingForOthers, answerSubmitted]);

  // Update when roomState.currentQuestion changes
  useEffect(() => {
    if (roomState.currentQuestion) {
      const newQuestionId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;
      const questionStartTime = (roomState as any).questionStartTime;
      // Use server timeRemaining or compute from questionStartTime so timer stays in sync after tab focus
      const serverTimeRemaining = calculateTimeRemaining(roomState.currentQuestion, questionStartTime);
      // Update if it's a different question OR if we don't have a current question yet (player joined mid-game)
      if (isNewQuestion || !currentQuestion) {
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        const questionWithId = {
          ...roomState.currentQuestion,
          questionId: newQuestionId,
        };
        setCurrentQuestion(questionWithId);
        setTimeRemaining(serverTimeRemaining);
        // Only reset question state if it's a new question, not if we're just syncing mid-game
        if (isNewQuestion) {
          resetQuestion();
        }
      } else {
        // Same question: sync timeRemaining (e.g. after tab focus / get-room-state)
        setTimeRemaining(serverTimeRemaining);
      }
    }
  }, [roomState.currentQuestion, (roomState as any).questionStartTime, currentQuestion]);

  useEffect(() => {
    const handleQuestion = (data: Question) => {
      console.log('CompetitivePlayerDisplay received question:', data);
      const newQuestionId = data.questionId || data.id;
      const questionWithId = {
        ...data,
        questionId: newQuestionId,
      };
      
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;
      
      if (isNewQuestion) {
        previousQuestionIdRef.current = newQuestionId;
        currentQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(questionWithId);
        // Use timeRemaining from server when present (reconnection); else timeLimit
        setTimeRemaining((data as any).timeRemaining ?? data.timeLimit);
        resetQuestion();
        previousPlayerScoresRef.current = new Map();
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      console.log('Question result received:', data);
      setAllPlayersAnswered(true);
      const allAnsweredIds = new Set<string>();
      data.submissions.forEach(s => allAnsweredIds.add(s.playerId));
      setAnsweredPlayerIds(allAnsweredIds);
      
      // If current player was the last one to answer, show flip immediately
      // This handles the case where they answered right before question-result
      if (answerSubmittedTimeRef.current) {
        const timeSinceSubmit = Date.now() - answerSubmittedTimeRef.current;
        // If less than 1.5 seconds since submission, they were the last player - flip immediately
        if (timeSinceSubmit < 1500) {
          setTimeout(() => {
            setShowInterestingFact(true);
          }, 500); // Quick 500ms delay for last player
        }
      }
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: {
        mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
        wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string }>;
        totalQuestions: number;
        averageScore: number;
      };
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setCurrentQuestion(null);
      setMyScore(0);
      resetQuestion();
      previousPlayerScoresRef.current = new Map();
    };

    const handleAnswerReceived = (data: { isCorrect: boolean; points: number; responseTime: number; questionId?: string }) => {
      const eventQuestionId = data.questionId || currentQuestionIdRef.current;
      // Only apply if this event is for the current question (ignore delayed/out-of-order from previous questions).
      if (currentQuestionIdRef.current && eventQuestionId !== currentQuestionIdRef.current) {
        return;
      }
      console.log('Answer received:', data);
      isSubmittingRef.current = false;
      setPointsThisQuestionAck(typeof data.points === 'number' ? data.points : null);
      // Don't add data.points here – score is synced from room-state (single source of truth). This avoids
      // wrong + green points when events are reordered or delayed (e.g. correct event from previous question).
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setWaitingForOthers(true);
      setShowInterestingFact(false);
      if (myPlayerId) {
        setAnsweredPlayerIds(prev => {
          const updated = new Set(prev);
          updated.add(myPlayerId);
          return updated;
        });
      }
      const submitTime = Date.now();
      answerSubmittedTimeRef.current = submitTime;
      const answeredQuestionId = eventQuestionId;
      setTimeout(() => {
        if (currentQuestionIdRef.current === answeredQuestionId) {
          setShowInterestingFact(true);
        }
      }, 1500); // 1.5s delay like SpeedChallenge
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);

    return () => {
      // Pass specific handler to only remove THIS component's listener, not App.tsx's
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
    };
  }, [socket, myPlayerId]);

  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !answerSubmitted) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) {
            return 0;
          }
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, answerSubmitted]);

  const handleSubmitAnswer = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!currentQuestion || answerSubmitted || isSubmittingRef.current || !socket || !roomState) {
      return;
    }
    
    // Check if this is a pair matching game
    const isPairMatching = roomState.gameType === 'pair-matching' || roomState.gameType === 'team-pair-matching' || roomState.gameType === 'party-pair-matching' || roomState.gameType === 'family-pair-matching';
    
    if (isPairMatching) {
      // For pair matching, check if all pairs are selected
      const allPairsSelected = currentQuestion.leftItems && 
        currentQuestion.leftItems.every(
          leftItem => selectedPairs[leftItem] && selectedPairs[leftItem] !== ''
        );
      if (!allPairsSelected) {
        return;
      }
    } else {
      // For other games, check selectedAnswer
      if (!selectedAnswer) {
        return;
      }
    }
    
    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('Question ID not found', currentQuestion);
      return;
    }
    
    isSubmittingRef.current = true;
    setAnswerSubmitted(true);
    
    const clientTimestamp = Date.now();
    
    // For pair matching, send pairs as JSON string
    let answer: string;
    if (isPairMatching && selectedPairs) {
      answer = JSON.stringify(selectedPairs);
    } else {
      answer = selectedAnswer || '';
    }
    
    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: questionId,
      answer: answer,
      timestamp: clientTimestamp,
    });
  };

  const handlePairSelect = (leftItem: string, rightItem: string) => {
    setSelectedPairs(prev => {
      const next = { ...prev };
      // When assigning a right item, ensure no other left key has it (single state update avoids double-selection bug)
      if (rightItem) {
        for (const key of Object.keys(next)) {
          if (key !== leftItem && next[key] === rightItem) {
            next[key] = '';
          }
        }
      }
      next[leftItem] = rightItem || '';
      return next;
    });
  };

  if (gameEnded) {
    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        setMyScore(0);
        resetQuestion();
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    const displayLeaderboard = finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? []);
    const displayStatistics = gameStatistics ?? gameEndData?.gameStatistics ?? null;
    return (
      <GameEndedScreen
        gameType={roomState.gameType}
        finalLeaderboard={displayLeaderboard}
        myPlayerId={myPlayerId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        showYouSuffix
        statistics={displayStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  if (!currentQuestion) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        isHost={isHost}
        onBackToGameSelection={onBackToGameSelection}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  const showWaiting = waitingForOthers && answerSubmitted;
  const sortedPlayers = roomState.players ? [...roomState.players].sort((a, b) => b.score - a.score) : [];

  return (
    <div className="app">
      <Header
        leftButton={getPlayerHeaderLeftButton({
          isHost,
          onBackToGameSelection,
          onGoHome,
          backTooltip: t('gameplay.backToGameSelection'),
          homeTooltip: t('gameplay.leaveGame'),
        })}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>{t('gameplay.roomLabel')}</span>
            <span style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold', 
              color: 'var(--color-primary)',
              padding: '0.3rem 0.6rem',
              backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
              borderRadius: '6px',
              border: '2px solid var(--color-primary)',
            }}>
              {roomState.roomName}
            </span>
          </div>
        }
      />
      <div className="container player-content-wrapper" style={{ paddingTop: '3rem' }}>
        {!showWaiting && (
          <>
            <ConditionalLeaderboard
              hostIsPlaying={hostIsPlaying}
              players={sortedPlayers}
              myPlayerId={myPlayerId}
            />
            
            <TimerBar
              timeRemaining={timeRemaining}
              timeLimit={currentQuestion.timeLimit}
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              showTimeCounter={false}
            />
            
            {/* Conditional Question Display - only shown when host is playing */}
            <ConditionalQuestionDisplay
              hostIsPlaying={hostIsPlaying}
              question={currentQuestion.question}
              imageUrl={currentQuestion.imageUrl}
              fallbackImageUrl={currentQuestion.fallbackImageUrl}
              visualContent={currentQuestion.visualContent}
              visualKind={currentQuestion.visualKind}
              visualItems={currentQuestion.visualItems}
              visualBehavior={currentQuestion.visualBehavior}
              stillItemIndex={currentQuestion.stillItemIndex}
              memoryHideAfterMs={currentQuestion.memoryHideAfterMs}
              memoryRevealAtMs={currentQuestion.memoryRevealAtMs}
              memoryRevealDurationMs={currentQuestion.memoryRevealDurationMs}
              questionStartTime={currentQuestion.startTime}
              timeLimit={currentQuestion.timeLimit}
              isPicturePerfect={currentQuestion.isPicturePerfect}
              gameType={roomState.gameType}
            />
            
            {/* Check if this is a pair matching game */}
            {(roomState.gameType === 'pair-matching' || roomState.gameType === 'team-pair-matching' || roomState.gameType === 'party-pair-matching' || roomState.gameType === 'family-pair-matching') &&
             currentQuestion.leftItems && currentQuestion.rightItems ? (
              <>
                <PairMatching
                  question={currentQuestion}
                  selectedPairs={selectedPairs}
                  onPairSelect={handlePairSelect}
                  answerSubmitted={answerSubmitted}
                  hostIsPlaying={false}
                  gameType={roomState.gameType}
                />
                {!answerSubmitted && (
                  <button
                    type="button"
                    className="button player-answer-submit-button"
                    onClick={handleSubmitAnswer}
                    disabled={
                      !currentQuestion.leftItems?.every(
                        leftItem => selectedPairs[leftItem] && selectedPairs[leftItem] !== ''
                      ) || !currentQuestion || answerSubmitted || isSubmittingRef.current
                    }
                    style={{
                      width: '100%',
                    }}
                  >
                    {t('gameplay.submitAnswer')}
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="options-list" style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.8rem',
                  margin: '2rem 0',
                }}>
                  {currentQuestion.options.map((option, index) => {
                    const isSelected = selectedAnswer === option;
                    const letter = String.fromCharCode(65 + index);
                    const isAreYouDrunk = roomState.gameType === 'are-you-drunk' || roomState.gameType === 'vision-check';
                    const questionId = currentQuestion.questionId ?? currentQuestion.id;
                    const drunkOptionClass = isAreYouDrunk ? pickOptionAnimationForIndex(questionId, index) : '';
                    const drunkOptionDelay = isAreYouDrunk ? pickOptionDelayForIndex(questionId, index) : 0;
                    return (
                      <div
                        key={index}
                        className={`option-item ${drunkOptionClass} ${isSelected ? 'option-item--selected' : ''} ${answerSubmitted ? 'option-item--submitted' : ''}`}
                        onClick={() => !answerSubmitted && setSelectedAnswer(option)}
                        style={{
                          cursor: answerSubmitted ? 'not-allowed' : 'pointer',
                          padding: '1rem',
                          fontSize: '1rem',
                          ...(isAreYouDrunk && drunkOptionClass ? { animationDelay: `${drunkOptionDelay}s` } : {}),
                        }}
                      >
                        {letter}. {option}
                      </div>
                    );
                  })}
                </div>
                {!answerSubmitted && (
                  <button
                    type="button"
                    className="button player-answer-submit-button"
                    onClick={handleSubmitAnswer}
                    disabled={!selectedAnswer || !currentQuestion || answerSubmitted || isSubmittingRef.current}
                    style={{
                      width: '100%',
                    }}
                  >
                    {t('gameplay.submitAnswer')}
                  </button>
                )}
              </>
            )}
          </>
        )}

        {showWaiting && (
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            overflowY: 'auto',
            paddingTop: '0.25rem',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
          }}>
            <ConditionalLeaderboard
              hostIsPlaying={hostIsPlaying}
              players={sortedPlayers}
              myPlayerId={myPlayerId}
              answeredPlayerIds={answeredPlayerIds}
              pointsThisQuestion={pointsThisQuestionAck}
              showQuestionPointsLine
            />

            {answerCorrect !== null && (
              <AnswerFeedbackCard
                isCorrect={answerCorrect}
                showDidYouKnow={showInterestingFact}
                interestingFact={currentQuestion?.interesting}
              />
            )}

            <WaitingSpinner isLoading={allPlayersAnswered} compact />

            {answerCorrect !== null && currentQuestion && (
              <div style={{ marginTop: 'auto', width: '100%' }}>
                <QuestionFeedbackSection
                  questionId={currentQuestion.questionId || currentQuestion.id || ''}
                  roomName={roomState.roomName}
                  gameType={roomState.gameType}
                  answerSubmitted={answerSubmitted}
                  showFeedback={true}
                  onSubmitFeedback={(rating, correct, questionText, correctAnswer) => {
                    if ((rating !== undefined || correct !== undefined) && socket) {
                      socket.emit('question-feedback', {
                        roomName: roomState.roomName,
                        questionId: currentQuestion.questionId || currentQuestion.id,
                        gameType: roomState.gameType,
                        rating: rating ?? undefined,
                        correct: correct ?? undefined,
                        questionText: questionText ?? currentQuestion.question,
                        correctAnswer: correctAnswer ?? currentQuestion.correctAnswer,
                      });
                    }
                  }}
                  questionText={currentQuestion.question}
                  correctAnswer={currentQuestion.correctAnswer}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitivePlayerDisplay;
